import * as React from "react";
import {api} from "../../../api/api";
import {EvChargingMeterPoint} from "../../../api/EvChargingApi";
import DataGrid from "../../../hiberUiKit/src/organisms/DataGrid/DataGrid";
import ButtonPrimary from "../../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import ButtonTertiary from "../../../hiberUiKit/src/atoms/Buttons/ButtonTertiary";
import IconWrapper from "../../../hiberUiKit/src/atoms/Icon/IconWrapper";
import {Icon} from "../../../hiberUiKit/src/atoms/Icon/Icon";
import {notifyBugsnagAndShowToast} from "../../../utils/Utils";
import {formatMprn} from "../evChargingBill";
import {evColors, evMessageStyles} from "../evChargingStyles";
import AddEvChargingMeterModal from "./AddEvChargingMeterModal";
import {PartnerBrandSubsectionTitle} from "../../admin/partnerBrands/sections/PartnerBrandSectionTitle";
import "./evChargingMeters.scss";

interface Props {
	partnerBrandId: string;
	partnerBrandName: string;
}

interface State {
	meterPoints?: EvChargingMeterPoint[];
	failed: boolean;
	adding: boolean;
	/**
	 * The meter whose name is being edited, with the name as typed.
	 */
	editing?: { id: string; name: string };
	savingName: boolean;
}

/**
 * The partner brand's EV charging meters, laid out like the users table under it. Its actions save at once, apart
 * from the form around it.
 */
export default class EvChargingMetersSection extends React.Component<Props, State> {
	state: State = {failed: false, adding: false, savingName: false};

	private unmounted = false;

	componentDidMount() {
		this.load();
	}

	componentWillUnmount() {
		this.unmounted = true;
	}

	render() {
		const {meterPoints, failed, adding} = this.state;
		return (
			<div style={styles.section}>
				<PartnerBrandSubsectionTitle title={"EV charging meters"}
											 aside={<ButtonPrimary label={"Add meter"} onClick={() => this.setState({adding: true})}/>}/>
				{failed && <div style={evMessageStyles.error}>The meters could not be loaded. Refresh the page to try again.</div>}
				{meterPoints &&
				<div style={styles.tableScroll} className={"c-ev-meters-table"}>
				<DataGrid>
					<thead>
					<tr>
						<th>MPRN</th>
						<th>Name</th>
						<th>EV charging only</th>
						<th style={styles.actionColumn}/>
					</tr>
					</thead>
					<tbody>
					{meterPoints.length === 0 &&
					<tr><td colSpan={4} style={styles.empty}>No meters.</td></tr>}
					{meterPoints.map(meterPoint => this.renderMeterPoint(meterPoint))}
					</tbody>
				</DataGrid>
				</div>}
				{adding &&
				<AddEvChargingMeterModal partnerBrandId={this.props.partnerBrandId}
										 partnerBrandName={this.props.partnerBrandName}
										 onDone={this.onAdded}
										 onClose={() => this.setState({adding: false})}/>}
			</div>
		);
	}

	private renderMeterPoint(meterPoint: EvChargingMeterPoint) {
		const {editing} = this.state;
		const editingThis = editing && editing.id === meterPoint.id;
		return (
			<tr key={meterPoint.id}>
				<td style={styles.mprn}>{formatMprn(meterPoint.mprn)}</td>
				{editingThis ? this.renderNameEditor(meterPoint) : <>
					<td className={meterPoint.nickname ? undefined : "is-empty"}>
						{meterPoint.nickname || <span style={{color: evColors.muted}}>-</span>}
					</td>
					{/* A mark rather than a checkbox: it cannot be changed here, and a checkbox would look as if it could.
					The words beside it show only on a phone, where there is no column header to say what it means. */}
					<td className={"c-ev-meters-table_dedicated"} style={styles.dedicated}>
						{meterPoint.dedicatedToEvCharging
							? <span title={"EV charging only"} style={styles.mark}>
								<IconWrapper icon={Icon.CheckMark} size={16} variant={"green"}/>
								<span className={"c-ev-meters-table_dedicated-text"}>EV charging only</span>
								<span className={"c-ev-meters-table_dedicated-desktop"} style={styles.srOnly}>Yes</span>
							</span>
							// Grey, not red: a shared meter is an ordinary state, not something wrong.
							: <span title={"Shared with other use"} style={{...styles.mark, ...styles.noMark}}>
								<span className={"c-ev-meters-table_dedicated-desktop"} aria-hidden={true} style={styles.mark}>
									<IconWrapper icon={Icon.X} size={16} variant={"gray"}/>
								</span>
								<span className={"c-ev-meters-table_dedicated-text"}>Shared with other use</span>
								<span className={"c-ev-meters-table_dedicated-desktop"} style={styles.srOnly}>No</span>
							</span>}
					</td>
					<td className={"t-right"}>
						<button type={"button"} style={styles.edit} disabled={!!editing}
								aria-label={`Rename meter ${formatMprn(meterPoint.mprn)}`} title={"Rename"}
								onClick={() => this.setState({editing: {id: meterPoint.id, name: meterPoint.nickname || ""}})}>
							<IconWrapper icon={Icon.Edit} size={14} variant={"gray"}/>
						</button>
					</td>
				</>}
			</tr>
		);
	}

	private renderNameEditor(meterPoint: EvChargingMeterPoint) {
		const {editing, savingName} = this.state;
		return (
			<td colSpan={3}>
				<div style={styles.editor}>
					<input className={"a-input"} style={styles.nameInput} value={editing!.name} maxLength={100} autoFocus
						   placeholder={"e.g. Rooftop chargers"} aria-label={`Name of meter ${formatMprn(meterPoint.mprn)}`}
						   onChange={e => this.setState({editing: {id: meterPoint.id, name: e.target.value}})}
						   onKeyDown={e => {
							   if (e.key === "Enter") {
								   e.preventDefault();
								   this.saveName();
							   }
							   if (e.key === "Escape") this.setState({editing: undefined});
						   }}/>
					<ButtonPrimary label={"Save"} onClick={this.saveName} isLoading={savingName} disabled={savingName}/>
					<ButtonTertiary label={"Cancel"} onClick={() => this.setState({editing: undefined})}
									disabled={savingName}/>
				</div>
			</td>
		);
	}

	private load() {
		api.instance.evChargingApi.getMeterPoints(this.props.partnerBrandId)
			.then(meterPoints => !this.unmounted && this.setState({meterPoints, failed: false}))
			.catch(error => {
				if (this.unmounted) return;
				this.setState({failed: true});
				notifyBugsnagAndShowToast(error, "Fetch EV charging meters");
			});
	}

	private saveName = () => {
		const {editing, savingName} = this.state;
		if (!editing || savingName) return;
		this.setState({savingName: true});
		api.instance.evChargingApi.renameMeterPoint(editing.id, editing.name.trim())
			.then(saved => {
				if (this.unmounted) return;
				this.setState(state => ({
					savingName: false,
					editing: undefined,
					meterPoints: state.meterPoints && state.meterPoints.map(m => m.id === saved.id ? saved : m)
				}));
			})
			.catch(error => {
				if (this.unmounted) return;
				this.setState({savingName: false});
				notifyBugsnagAndShowToast(error, "Rename EV charging meter");
			});
	};

	/**
	 * Reloaded rather than inserted, so the list keeps the backend's order by MPRN.
	 */
	private onAdded = () => {
		this.setState({adding: false});
		this.load();
	};
}

const styles: { [key: string]: React.CSSProperties } = {
	section: {paddingBottom: 30},
	// A phone is narrower than the table: it scrolls sideways instead of pushing the page wider.
	tableScroll: {overflowX: "auto"},
	empty: {textAlign: "center", color: evColors.muted},
	mprn: {fontWeight: 600, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap"},
	actionColumn: {width: 56},
	dedicated: {whiteSpace: "nowrap"},
	mark: {display: "inline-flex", alignItems: "center", gap: 6, verticalAlign: "middle"},
	noMark: {color: evColors.muted},
	srOnly: {position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap"},
	edit: {border: 0, background: "transparent", padding: "4px 8px", color: evColors.navy, cursor: "pointer"},
	editor: {display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12},
	nameInput: {width: 300, maxWidth: "100%"}
};
